import { Type, ButtonPrimary } from '@spotify-internal/encore-web';
import {
  spacer8,
  spacer12,
  spacer16,
  spacer20,
  spacer24,
  spacer32,
  spacer40,
  spacer48,
  display1FontSizeXSmall,
  screenSmMax,
  screenXsMax,
  heading2FontSize,
  white,
} from '@spotify-internal/encore-foundation';
import styled, { css } from 'styled-components';
import { hexToRgba } from '@mrkt/features/color-conversion';
import {
  Box,
  ImageAspectBox16x9,
  ButtonTertiaryAsLink,
  UnstyledLink,
  withAnimationContext,
  DELAYS,
} from '../ui';
import { transformScaleUp } from '../utils';
import { fadeInAnimation } from '../ui/Animations';

export const Wrapper = styled(Box)`
  p {
    padding-top: ${spacer16};
  }
`;

export const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    variant: Type.heading1,
    weight: Type.bold,
  })`
    ${fadeInAnimation(DELAYS.SECOND)};
    padding-top: ${spacer8};
    padding-bottom: ${spacer8};
    font-size: ${display1FontSizeXSmall};
    max-width: 684px;

    @media (max-width: ${screenSmMax}) {
      text-align: left;
      font-size: ${heading2FontSize};
      padding-top: 0;
      padding-bottom: ${spacer8};
    }
  `,
);

export const Subtext = withAnimationContext(
  styled(Type.p).attrs({
    variant: Type.body1,
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
  `,
);

export const CTALink = styled(ButtonTertiaryAsLink)`
  padding-left: 0;
  text-align: left;
  color: ${props => props.theme.link?.color};
  svg {
    margin-left: ${spacer8};
    vertical-align: -2px;
  }
  &&:hover:not(:disabled),
  &:focus:not(:disabled),
  &:active:not(:disabled) {
    color: ${props => hexToRgba(props.theme.link?.color, 0.5)};
    outline: none;
  }
`;

export const ItemList = styled.ul`
  margin-top: ${spacer40};
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-content: flex-start;
  width: 100%;
  padding-left: 0;
  @media (max-width: ${screenSmMax}) {
    margin-top: ${spacer24};
  }
`;

export const Item = styled.li<{ featured: boolean }>`
  margin: 0 ${spacer20} ${spacer48} 0;
  list-style: none;

  /* 3-up default */
  width: ${props =>
    props.featured ? `calc(50% - ${spacer20})` : `calc(33.33% - ${spacer20})`};

  /* 2-up on tablet */
  @media (max-width: ${screenSmMax}) {
    width: calc(50% - ${spacer20});
  }

  /* 1-up on mobile */
  @media (max-width: ${screenXsMax}) {
    width: 100%;
    margin-bottom: ${spacer32};
  }
`;

type ImageWrapperType = { video: boolean; featured: boolean };

export const ImageWrapper = styled(ImageAspectBox16x9)<ImageWrapperType>`
  overflow: hidden;

  /* for videos add a black gradient overlay at the bottom to make the play button
     slightly more visible */
  ${props =>
    props.video &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        height: 100%;
        width: 100%;
        box-shadow: inset 0 -90px 10px 0 rgba(0, 0, 0, 0.1);
        z-index: 2;
      }
    `}

  /* play icon */
  svg {
    display: ${props => (props.video ? 'initial' : 'none')};
    position: absolute;
    bottom: ${props => (props.featured ? spacer24 : spacer16)};
    left: ${props => (props.featured ? spacer24 : spacer16)};
    width: ${props => (props.featured ? '48px' : '32px')};
    height: auto;
    color: ${white};
    z-index: 3;

    @media (max-width: ${screenXsMax}) {
      bottom: ${spacer12};
      left: ${spacer12};
    }

    @media (max-width: ${screenSmMax}) {
      bottom: ${spacer16};
      left: ${spacer16};
    }
  }

  &:hover {
    img,
    svg {
      ${transformScaleUp};
      transition: transform 0.3s ease-in-out;
    }
    svg {
      opacity: 0.8;
    }
  }
`;

export const ItemLabel = withAnimationContext(
  styled(Type.p).attrs({
    variant: Type.body3,
    weight: Type.book,
  })`
    ${fadeInAnimation(DELAYS.FOURTH)};
    padding-top: ${spacer20};
    padding-bottom: 0;
    text-transform: uppercase;
    color: ${props => props.theme.label.color};
  `,
);

export const ItemTitle = withAnimationContext(
  styled(Type.h3).attrs({
    variant: Type.heading3,
    weight: Type.bold,
  })`
    ${fadeInAnimation(DELAYS.FIFTH)};
    padding-top: ${spacer16};
    padding-bottom: ${spacer8};
    max-height: 3.6em;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    @media (max-width: ${screenSmMax}) {
      padding-top: ${spacer8};
      padding-bottom: 0;
    }
  `,
);

export const ItemText = withAnimationContext(
  styled(Type.p).attrs({
    variant: Type.body1,
  })`
    ${fadeInAnimation(DELAYS.SIXTH)};
    /* no description text on mobile */
    @media (max-width: ${screenXsMax}) {
      display: none;
    }
  `,
);

export const StyledLink = styled(UnstyledLink)<any>`
  text-decoration: none;
  color: ${props => props.theme.link?.color || white};

  &:hover,
  &:focus:not(:disabled),
  &:active:not(:disabled) {
    text-decoration: none;
    color: ${props => hexToRgba(props.theme.link?.color || white, 0.5)};
  }

  &:hover {
    img,
    svg {
      ${transformScaleUp};
      transition: transform 0.3s ease-in-out;
    }
    svg {
      opacity: 0.8;
    }
  }
`;

export const LoadMoreButton = styled(ButtonPrimary)``;

export const LoadMoreButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
  margin-bottom: ${spacer20};
`;
